'use client';

import { AlertTriangle, BarChart3, CheckCircle, ExternalLink, Eye, Search } from 'lucide-react';
import { useState } from 'react';

interface SlugAnalysis {
  slug: string;
  duplicates: string[];
  suggestions: string[];
  isUnique: boolean;
  totalExisting?: number;
  analysis?: {
    length: number;
    wordCount: number;
    hasNumbers: boolean;
    hasSpecialChars: boolean;
  };
}

interface MetaPreview {
  title: string;
  description: string;
  url: string;
  googlePreview: {
    title: string;
    url: string;
    description: string;
  };
  socialPreview: {
    title: string;
    description: string;
    image: string;
  };
}

interface KeywordAnalysis {
  keyword: string;
  density: number;
  occurrences: number;
  totalWords: number;
  suggestions: string[];
  positions?: number[];
  sentiment?: 'low' | 'optimal' | 'high';
}

export default function SEOEnhancements() {
  const [activeTab, setActiveTab] = useState<'slug' | 'meta' | 'keywords'>('slug');
  const [slugInput, setSlugInput] = useState('');
  const [slugAnalysis, setSlugAnalysis] = useState<SlugAnalysis | null>(null);
  const [metaInput, setMetaInput] = useState<MetaPreview>({
    title: '',
    description: '',
    url: '',
    googlePreview: { title: '', url: '', description: '' },
    socialPreview: { title: '', description: '', image: '' }
  });
  const [contentInput, setContentInput] = useState('');
  const [focusKeyword, setFocusKeyword] = useState('');
  const [keywordAnalysis, setKeywordAnalysis] = useState<KeywordAnalysis | null>(null);
  const [allKeywords, setAllKeywords] = useState<KeywordAnalysis[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const analyzeSlug = async () => {
    if (!slugInput.trim()) {
      setError('Please enter a slug to analyze');
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setSlugAnalysis(null);

    try {
      const response = await fetch('/api/admin/seo/slug-analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ slug: slugInput }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to analyze slug');
      }

      setSlugAnalysis(data);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      setError(`Slug Analysis Error: ${errorMessage}`);
      console.error('Error analyzing slug:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const analyzeKeywords = async () => {
    if (!contentInput.trim()) {
      setError('Please enter content to analyze');
      return;
    }

    if (!focusKeyword.trim()) {
      setError('Please enter a focus keyword');
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setKeywordAnalysis(null);
    setAllKeywords([]);

    try {
      // Analyze content
      const words = contentInput.toLowerCase().split(/\s+/);
      const wordCount = words.length;

      // Normalize focus keyword
      const normalizedFocusKeyword = focusKeyword.toLowerCase().trim();
      const focusKeywordWords = normalizedFocusKeyword.split(/\s+/);

      // Find all occurrences of focus keyword
      let occurrences = 0;
      const positions: number[] = [];

      if (focusKeywordWords.length === 1) {
        // Single word keyword
        words.forEach((word, index) => {
          const cleanWord = word.replace(/[^\w]/g, '');
          if (cleanWord === normalizedFocusKeyword) {
            occurrences++;
            positions.push(index);
          }
        });
      } else {
        // Multi-word keyword (phrase)
        const contentLower = contentInput.toLowerCase();
        let index = contentLower.indexOf(normalizedFocusKeyword);
        while (index !== -1) {
          occurrences++;
          // Approximate word position
          const wordsBeforePhrase = contentLower.substring(0, index).split(/\s+/).length - 1;
          positions.push(wordsBeforePhrase);
          index = contentLower.indexOf(normalizedFocusKeyword, index + 1);
        }
      }

      const density = wordCount > 0 ? (occurrences / wordCount) * 100 : 0;

      // Determine sentiment
      let sentiment: 'low' | 'optimal' | 'high' = 'low';
      if (density >= 1 && density <= 3) {
        sentiment = 'optimal';
      } else if (density > 3) {
        sentiment = 'high';
      }

      // Generate suggestions
      const suggestions = getKeywordSuggestions(normalizedFocusKeyword);

      const focusAnalysis: KeywordAnalysis = {
        keyword: normalizedFocusKeyword,
        density,
        occurrences,
        totalWords: wordCount,
        positions,
        sentiment,
        suggestions
      };

      setKeywordAnalysis(focusAnalysis);

      // Also generate analysis for other top keywords
      const wordFrequency: { [key: string]: number } = {};
      words.forEach(word => {
        const cleanWord = word.replace(/[^\w]/g, '');
        if (cleanWord.length > 3 && cleanWord !== normalizedFocusKeyword) {
          wordFrequency[cleanWord] = (wordFrequency[cleanWord] || 0) + 1;
        }
      });

      const otherKeywords = Object.entries(wordFrequency)
        .filter(([, count]) => count > 1)
        .map(([word, count]) => {
          const keywordDensity = (count / wordCount) * 100;
          let keywordSentiment: 'low' | 'optimal' | 'high' = 'low';
          if (keywordDensity >= 1 && keywordDensity <= 3) {
            keywordSentiment = 'optimal';
          } else if (keywordDensity > 3) {
            keywordSentiment = 'high';
          }

          return {
            keyword: word,
            density: keywordDensity,
            occurrences: count,
            totalWords: wordCount,
            sentiment: keywordSentiment,
            suggestions: []
          };
        })
        .sort((a, b) => b.density - a.density)
        .slice(0, 10);

      setAllKeywords(otherKeywords);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      setError(`Keyword Analysis Error: ${errorMessage}`);
      console.error('Error analyzing keywords:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getKeywordSuggestions = (keyword: string): string[] => {
    const suggestions = [
      `${keyword} guide`,
      `${keyword} tutorial`,
      `${keyword} tips`,
      `${keyword} examples`,
      `${keyword} best practices`
    ];
    return suggestions.slice(0, 3);
  };


  const tabs = [
    { id: 'slug', label: 'Slug Analyzer', icon: Search },
    { id: 'meta', label: 'Meta Preview', icon: Eye },
    { id: 'keywords', label: 'Keyword Analysis', icon: BarChart3 }
  ];

  return (
    <div className="space-y-6">
      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-red-900 font-medium">Error</h4>
            <p className="text-red-800 text-sm mt-1">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800 text-xl leading-none"
            aria-label="Close error"
          >
            ×
          </button>
        </div>
      )}

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id as 'slug' | 'meta' | 'keywords');
                  setError(null);
                }}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <Icon className="inline w-4 h-4 mr-2" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Slug Analyzer Tab */}
      {activeTab === 'slug' && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">Slug Analyzer</h3>
            <p className="text-blue-800 text-sm">
              Check for duplicate slugs and get suggestions for better SEO optimization.
            </p>
          </div>

          <div className="flex gap-3">
            <input
              type="text"
              value={slugInput}
              onChange={(e) => setSlugInput(e.target.value)}
              placeholder="Enter slug to analyze (e.g., my-page-title)"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none "
            />
            <button
              onClick={analyzeSlug}
              disabled={isAnalyzing || !slugInput.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isAnalyzing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Analyzing...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4" />
                  Analyze
                </>
              )}
            </button>
          </div>

          {slugAnalysis && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                {slugAnalysis.isUnique ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                )}
                <h4 className="text-lg font-semibold">
                  {slugAnalysis.isUnique ? 'Slug is Unique!' : 'Duplicate Found'}
                </h4>
              </div>

              {/* Slug Analysis Details */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h5 className="font-medium text-gray-900 mb-3">Slug Analysis</h5>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Length:</span>
                    <div className="font-medium">{slugAnalysis.analysis?.length || slugAnalysis.slug.length} characters</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Words:</span>
                    <div className="font-medium">{slugAnalysis.analysis?.wordCount || slugAnalysis.slug.split('-').length}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Contains Numbers:</span>
                    <div className="font-medium">{slugAnalysis.analysis?.hasNumbers ? 'Yes' : 'No'}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Special Characters:</span>
                    <div className="font-medium">{slugAnalysis.analysis?.hasSpecialChars ? 'Yes' : 'No'}</div>
                  </div>
                </div>
              </div>

              {!slugAnalysis.isUnique && slugAnalysis.duplicates.length > 0 && (
                <div className="mb-6">
                  <h5 className="font-medium text-gray-900 mb-3">Duplicate/Similar Slugs:</h5>
                  <div className="space-y-2">
                    {slugAnalysis.duplicates.map((duplicate, index) => (
                      <div key={index} className="flex items-center gap-2 text-red-600">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="font-mono bg-red-50 px-2 py-1 rounded">{duplicate}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h5 className="font-medium text-gray-900 mb-3">Suggested Alternatives:</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {slugAnalysis.suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => setSlugInput(suggestion)}
                      className="text-left p-3 border border-gray-200 rounded-md hover:border-blue-300 hover:bg-blue-50 transition-colors"
                    >
                      <div className="font-mono text-sm text-blue-600">{suggestion}</div>
                      <div className="text-xs text-gray-500 mt-1">Click to use</div>
                    </button>
                  ))}
                </div>
              </div>

              {slugAnalysis.totalExisting && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-500">
                    Total existing slugs in database: <span className="font-medium">{slugAnalysis.totalExisting}</span>
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Meta Preview Tab */}
      {activeTab === 'meta' && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-green-900 mb-2">Meta Preview Tool</h3>
            <p className="text-green-800 text-sm">
              Preview how your content will appear in Google search results and social media shares.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Form */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Page Title (max 60 characters)
                </label>
                <input
                  type="text"
                  value={metaInput.title}
                  onChange={(e) => setMetaInput({ ...metaInput, title: e.target.value })}
                  maxLength={60}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter your page title"
                />
                <div className="text-xs text-gray-500 mt-1">
                  {metaInput.title.length}/60 characters
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Meta Description (max 160 characters)
                </label>
                <textarea
                  value={metaInput.description}
                  onChange={(e) => setMetaInput({ ...metaInput, description: e.target.value })}
                  maxLength={160}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter your meta description"
                />
                <div className="text-xs text-gray-500 mt-1">
                  {metaInput.description.length}/160 characters
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL
                </label>
                <input
                  type="url"
                  value={metaInput.url}
                  onChange={(e) => setMetaInput({ ...metaInput, url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="https://example.com/page"
                />
              </div>
            </div>

            {/* Preview Section */}
            <div className="space-y-4">
              {/* Google Preview */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <ExternalLink className="w-4 h-4" />
                  Google Search Preview
                </h4>
                <div className="bg-white border border-gray-200 rounded p-3">
                  <div className="text-blue-600 text-sm mb-1">
                    {metaInput.url || 'example.com/page'}
                  </div>
                  <div className="text-blue-800 font-medium text-lg mb-1 line-clamp-1">
                    {metaInput.title || 'Your page title will appear here'}
                  </div>
                  <div className="text-gray-600 text-sm line-clamp-2">
                    {metaInput.description || 'Your meta description will appear here. This is how your page will look in Google search results.'}
                  </div>
                </div>
              </div>

              {/* Social Media Preview */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Social Media Preview
                </h4>
                <div className="bg-white border border-gray-200 rounded p-3">
                  <div className="w-full h-32 bg-gray-100 rounded mb-3 flex items-center justify-center">
                    <span className="text-gray-500 text-sm">Preview Image</span>
                  </div>
                  <div className="font-medium text-gray-900 mb-1 line-clamp-2">
                    {metaInput.title || 'Your page title'}
                  </div>
                  <div className="text-gray-600 text-sm line-clamp-3">
                    {metaInput.description || 'Your meta description for social media sharing'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Keyword Analysis Tab */}
      {activeTab === 'keywords' && (
        <div className="space-y-4">
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-purple-900 mb-2">Focus Keyword Analysis</h3>
            <p className="text-purple-800 text-sm">
              Enter a focus keyword and analyze its usage and density in your content for better SEO optimization.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Focus Keyword <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={focusKeyword}
              onChange={(e) => setFocusKeyword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="e.g., SEO optimization or digital marketing"
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter the main keyword or phrase you want to analyze in your content
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content to Analyze <span className="text-red-500">*</span>
            </label>
            <textarea
              value={contentInput}
              onChange={(e) => setContentInput(e.target.value)}
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="Paste your content here to analyze keyword density..."
            />
          </div>

          <button
            onClick={analyzeKeywords}
            disabled={isAnalyzing || !contentInput.trim() || !focusKeyword.trim()}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isAnalyzing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Analyzing...
              </>
            ) : (
              <>
                <BarChart3 className="w-4 h-4" />
                Analyze Keyword
              </>
            )}
          </button>

          {keywordAnalysis && (
            <div className="space-y-6">
              {/* Focus Keyword Analysis */}
              <div className="bg-white border-2 border-purple-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-900">Focus Keyword: &quot;{keywordAnalysis.keyword}&quot;</h4>
                  {keywordAnalysis.sentiment === 'optimal' && (
                    <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      Optimal
                    </span>
                  )}
                  {keywordAnalysis.sentiment === 'low' && (
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" />
                      Too Low
                    </span>
                  )}
                  {keywordAnalysis.sentiment === 'high' && (
                    <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" />
                      Too High
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="text-sm text-purple-600 mb-1">Occurrences</div>
                    <div className="text-3xl font-bold text-purple-900">{keywordAnalysis.occurrences}</div>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="text-sm text-purple-600 mb-1">Keyword Density</div>
                    <div className="text-3xl font-bold text-purple-900">{keywordAnalysis.density.toFixed(2)}%</div>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="text-sm text-purple-600 mb-1">Total Words</div>
                    <div className="text-3xl font-bold text-purple-900">{keywordAnalysis.totalWords}</div>
                  </div>
                </div>

                {/* Density Indicator */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Density Level</span>
                    <span className="text-sm text-gray-600">{keywordAnalysis.density.toFixed(2)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 relative">
                    <div
                      className={`h-3 rounded-full ${keywordAnalysis.sentiment === 'optimal'
                        ? 'bg-green-600'
                        : keywordAnalysis.sentiment === 'high'
                          ? 'bg-red-600'
                          : 'bg-yellow-600'
                        }`}
                      style={{ width: `${Math.min(keywordAnalysis.density * 10, 100)}%` }}
                    ></div>
                    {/* Optimal range indicators */}
                    <div className="absolute top-0 left-[10%] w-0.5 h-3 bg-green-400"></div>
                    <div className="absolute top-0 left-[30%] w-0.5 h-3 bg-green-400"></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0%</span>
                    <span className="text-green-600">1-3% (Optimal)</span>
                    <span>10%+</span>
                  </div>
                </div>

                {/* Recommendations */}
                <div className="p-4 bg-gray-50 rounded-lg mb-4">
                  <h5 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    Recommendations
                  </h5>
                  <div className="text-sm text-gray-700">
                    {keywordAnalysis.sentiment === 'optimal' && (
                      <p className="text-green-700">
                        <strong>Excellent!</strong> Your keyword density is in the optimal range (1-3%).
                        This is considered ideal for SEO without appearing as keyword stuffing.
                      </p>
                    )}
                    {keywordAnalysis.sentiment === 'low' && (
                      <p className="text-yellow-700">
                        <strong>Increase Usage:</strong> Your keyword density is below 1%.
                        Consider adding &quot;{keywordAnalysis.keyword}&quot; more naturally throughout your content
                        to improve SEO relevance.
                      </p>
                    )}
                    {keywordAnalysis.sentiment === 'high' && (
                      <p className="text-red-700">
                        <strong>Reduce Usage:</strong> Your keyword density is above 3%.
                        This may be flagged as keyword stuffing by search engines.
                        Consider using synonyms and related terms instead.
                      </p>
                    )}
                  </div>
                </div>

                {/* Keyword Suggestions */}
                {keywordAnalysis.suggestions.length > 0 && (
                  <div>
                    <h5 className="font-medium text-gray-900 mb-3">Related Keyword Suggestions</h5>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {keywordAnalysis.suggestions.map((suggestion, idx) => (
                        <div
                          key={idx}
                          className="p-3 border border-purple-200 rounded-md bg-purple-50 text-purple-900 text-sm"
                        >
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Other Keywords Found */}
              {allKeywords.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Other Keywords Found</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Keyword
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Occurrences
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Density
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {allKeywords.map((analysis, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="font-medium text-gray-900">{analysis.keyword}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {analysis.occurrences}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <div className="w-20 bg-gray-200 rounded-full h-2">
                                  <div
                                    className={`h-2 rounded-full ${analysis.sentiment === 'optimal'
                                      ? 'bg-green-600'
                                      : analysis.sentiment === 'high'
                                        ? 'bg-red-600'
                                        : 'bg-yellow-600'
                                      }`}
                                    style={{ width: `${Math.min(analysis.density * 10, 100)}%` }}
                                  ></div>
                                </div>
                                <span className="text-sm text-gray-600">{analysis.density.toFixed(2)}%</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {analysis.sentiment === 'optimal' && (
                                <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">Optimal</span>
                              )}
                              {analysis.sentiment === 'low' && (
                                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs">Low</span>
                              )}
                              {analysis.sentiment === 'high' && (
                                <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs">High</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Analysis Tips */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h5 className="font-medium text-blue-900 mb-2">SEO Tips</h5>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                  <li>Optimal keyword density is typically between 1-3%</li>
                  <li>Use your focus keyword naturally in titles, headings, and throughout the content</li>
                  <li>Include related keywords and synonyms to avoid keyword stuffing</li>
                  <li>Place keywords in the first 100 words of your content when possible</li>
                  <li>Use long-tail variations of your focus keyword for better targeting</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
